import { DocumentNode, useApolloClient } from '@apollo/client';
import { sortBy, throttle } from 'lodash';
import { useCallback, useEffect, useState } from 'react';

import { useCurrentRefs } from 'src/hooks/useCurrentRefs';
import { v4 } from 'uuid';

type TUseSubscribeToQueryProps<TQueryResult, TVars> = {
  queryDoc: DocumentNode;
  subscriptionDoc: DocumentNode;
  variables: TVars;
  onQueryCompleted?(payload: TQueryResult): void;
  updateQuery(prev: TQueryResult, next: { subscriptionData: any }): TQueryResult;
};
const fetchPolicy = 'no-cache';

const ACTIVE_QUERIES = {};

const EVENT_NAME = 'cache-updated' + v4();

const CACHE = new Proxy(
  {},
  {
    set: function (obj, cacheID, value) {
      obj[cacheID] = value;
      window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: { cacheID } }));
      return true;
    },
  },
);

export function useSubscribeToQuery<TQueryResult = any, TVars = any>({
  queryDoc,
  subscriptionDoc,
  variables,
  onQueryCompleted,
  updateQuery,
}: TUseSubscribeToQueryProps<TQueryResult, TVars>): {
  data: TQueryResult;
  loading: boolean;
  error: string;
} {
  const variablesID = sortBy(Object.entries(variables), 0)
    .map((p) => p[1])
    .join(':');
  const cacheID = `${(queryDoc.definitions[0] as any).name!.value!}:${variablesID}`;
  const client = useApolloClient();
  const [data, setData] = useState<TQueryResult>(CACHE[cacheID]);
  const [loading, setLoading] = useState(!data);
  const [error, setError] = useState('');
  const currentRefs = useCurrentRefs({
    client,
    variables,
    updateQuery,
    onQueryCompleted,
    queryDoc,
    cacheID,
    subscriptionDoc,
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const setDataThrottled = useCallback(
    throttle(
      (newData) => {
        setData(newData);
      },
      2000, // One update per 2 sec is enough 👍
    ),
    [],
  );

  const subscribe = useCallback(
    ({
      subscriptionDoc,
      variables,
      cacheID,
    }: Pick<TUseSubscribeToQueryProps<TQueryResult, TVars>, 'subscriptionDoc' | 'variables'> & {
      cacheID: string;
    }) => {
      const { client, updateQuery } = currentRefs.current;

      const subscription = client
        .subscribe<any, TVars>({
          query: subscriptionDoc,
          variables,
          fetchPolicy,
        })
        .subscribe({
          next(subscriptionData) {
            const prev = CACHE[cacheID];
            CACHE[cacheID] = updateQuery(prev || {}, { subscriptionData });
            setDataThrottled(CACHE[cacheID]);
          },
          error(err) {
            // eslint-disable-next-line no-console
            console.error('Error happened inside a file: useSubscribeToQuery.ts\n\n\n' + err);
          },
          complete() {
            //
          },
        });

      return () => {
        subscription.unsubscribe();
      };
    },
    [currentRefs, setDataThrottled],
  );

  useEffect(() => {
    function handler({ detail }) {
      const { cacheID } = currentRefs.current;

      if (detail.cacheID === cacheID) {
        setData(CACHE[cacheID]);
        setLoading(false);
        setError('');
      }
    }

    // @ts-ignore
    window.addEventListener(EVENT_NAME, handler);

    return () => {
      // @ts-ignore
      window.removeEventListener(EVENT_NAME, handler);
    };
  }, [currentRefs]);

  useEffect(() => {
    async function initFetch() {
      const {
        client,
        variables,
        queryDoc,
        cacheID,
        subscriptionDoc,
        onQueryCompleted,
      } = currentRefs.current;
      const isEveryVarTruthy = Object.values(variables).every(Boolean);

      setData(CACHE[cacheID]);

      if (!isEveryVarTruthy) {
        setLoading(false);
        return;
      }

      if (ACTIVE_QUERIES[cacheID]?.count) {
        ACTIVE_QUERIES[cacheID].count += 1;
        return;
      }

      setError('');
      setLoading(true);

      try {
        ACTIVE_QUERIES[cacheID] = { count: 1 };

        const { data } = await client.query({ query: queryDoc, variables, fetchPolicy });

        onQueryCompleted?.(data);

        CACHE[cacheID] = data;
        setData(CACHE[cacheID]);

        const unsubscribe = subscribe({ subscriptionDoc, variables, cacheID });

        ACTIVE_QUERIES[cacheID].unsubscribe = unsubscribe;
      } catch (error) {
        CACHE[cacheID] = undefined;
        setData(CACHE[cacheID]);
        setError(error);
      }

      setLoading(false);
    }

    initFetch();
  }, [currentRefs, subscribe, cacheID]);

  useEffect(
    () => () => {
      if (!ACTIVE_QUERIES[cacheID]) return;

      ACTIVE_QUERIES[cacheID].count -= 1;

      if (!ACTIVE_QUERIES[cacheID].count) {
        ACTIVE_QUERIES[cacheID].unsubscribe?.();
        delete ACTIVE_QUERIES[cacheID];
      }
    },
    [cacheID],
  );

  return {
    data,
    loading,
    error,
  };
}
